import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { productService, Product } from '../services/productService';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { addToCart } from '../../../shared/store/cartSlice';
import { toggleWishlist, selectIsWishlisted } from '../../../shared/store/wishlistSlice';

const API_STATIC = (process.env.REACT_APP_API_URL ?? 'http://localhost:8000/api/v1').replace(/\/api\/v1.*$/, '');
const ZOOM_SCALE = 2.5;

/* ── Image zoom component ─────────────────────────────────────────── */
const ImageZoom: React.FC<{ src: string; alt: string; onClick: () => void }> = ({ src, alt, onClick }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [hovering, setHovering]   = useState(false);
    const [panning, setPanning]     = useState(false);
    const [bgPos, setBgPos]         = useState('50% 50%');

    const handleMouseEnter = () => { setHovering(true); setPanning(false); };
    const handleMouseLeave = () => { setHovering(false); setPanning(false); setBgPos('50% 50%'); };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = (((e.clientX - rect.left) / rect.width) * 100).toFixed(2);
        const y = (((e.clientY - rect.top)  / rect.height) * 100).toFixed(2);
        setBgPos(`${x}% ${y}%`);
        if (!panning) setPanning(true);
    };

    const transition = panning ? 'none' : 'background-size 0.35s ease, background-position 0.35s ease';

    return (
        <div style={{ position: 'relative', width: '100%', aspectRatio: '1/1' }}>
            <div
                ref={containerRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                onClick={onClick}
                style={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${src})`,
                    backgroundSize:     hovering ? `${ZOOM_SCALE * 100}%` : '100%',
                    backgroundPosition: hovering ? bgPos : 'center',
                    backgroundRepeat: 'no-repeat',
                    cursor: hovering ? 'crosshair' : 'zoom-in',
                    transition,
                    borderRadius: '12px 0 0 12px',
                    backgroundColor: '#f0f4f8',
                }}
                role="img"
                aria-label={alt}
            />

            {/* "Click to enlarge" hint — fades in on hover */}
            <div style={{
                position: 'absolute',
                bottom: 14,
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(0,0,0,0.55)',
                color: '#fff',
                fontSize: '0.75rem',
                fontWeight: 600,
                padding: '5px 14px',
                borderRadius: 20,
                pointerEvents: 'none',
                opacity: hovering ? 1 : 0,
                transition: 'opacity 0.2s ease',
                whiteSpace: 'nowrap',
                letterSpacing: '0.04em',
            }}>
                🔍 Click to enlarge
            </div>
        </div>
    );
};

/* ── Lightbox component ───────────────────────────────────────────── */
const Lightbox: React.FC<{ src: string; alt: string; onClose: () => void }> = ({ src, alt, onClose }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [onClose]);

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.88)',
                zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24,
                animation: 'lbFadeIn 0.2s ease',
            }}
        >
            {/* Close button */}
            <button
                onClick={onClose}
                style={{
                    position: 'absolute', top: 20, right: 24,
                    background: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: '#fff', borderRadius: '50%',
                    width: 44, height: 44, fontSize: '1.4rem',
                    cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1,
                    transition: 'background 0.15s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.3)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.15)'; }}
                aria-label="Close"
            >
                ×
            </button>

            {/* Image — stop propagation so click on image doesn't close */}
            <img
                src={src}
                alt={alt}
                onClick={e => e.stopPropagation()}
                style={{
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    objectFit: 'contain',
                    borderRadius: 8,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                    animation: 'lbScaleIn 0.25s ease',
                    userSelect: 'none',
                }}
            />

            {/* ESC hint */}
            <div style={{
                position: 'absolute', bottom: 20, left: '50%',
                transform: 'translateX(-50%)',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.78rem',
                pointerEvents: 'none',
            }}>
                Press Esc or click outside to close
            </div>

            <style>{`
                @keyframes lbFadeIn  { from { opacity: 0; }               to { opacity: 1; } }
                @keyframes lbScaleIn { from { transform: scale(0.92); opacity: 0; } to { transform: scale(1); opacity: 1; } }
            `}</style>
        </div>
    );
};

/* ── Main page ────────────────────────────────────────────────────── */
const ProductDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const dispatch = useAppDispatch();
    const [product, setProduct]       = useState<Product | null>(null);
    const [loading, setLoading]       = useState(true);
    const [notFound, setNotFound]     = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [added, setAdded]           = useState(false);
    const wishlisted = useAppSelector(selectIsWishlisted(id ?? ''));

    const handleAddToCart = () => {
        if (!product) return;
        dispatch(addToCart({ productId: product._id, name: product.name, brand: product.brand, price: product.price, image: product.image, category: product.category }));
        setAdded(true);
        setTimeout(() => setAdded(false), 1500);
    };

    const handleWishlist = () => {
        if (!product) return;
        dispatch(toggleWishlist({ productId: product._id, name: product.name, brand: product.brand, price: product.price, image: `${API_STATIC}${product.image}`, category: product.category }));
    };

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        productService.getPublicById(id)
            .then(res => {
                setProduct(res.data.product);
                setLoading(false);
            })
            .catch(err => {
                if (err?.response?.status === 404) setNotFound(true);
                setLoading(false);
            });
    }, [id]);

    if (loading) return (
        <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontFamily: "'Open Sans', sans-serif" }}>
            Loading product…
        </div>
    );

    if (notFound || !product) return (
        <div style={{ minHeight: '50vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'Open Sans', sans-serif", textAlign: 'center', padding: 40 }}>
            <h2 style={{ fontSize: '2rem', color: '#1a202c', marginBottom: 8 }}>Product Not Found</h2>
            <p style={{ color: '#64748b', marginBottom: 24 }}>This product may have been removed or is no longer available.</p>
            <Link to="/products" style={{ color: '#1565c0', fontWeight: 600, textDecoration: 'none' }}>
                ← Back to Products
            </Link>
        </div>
    );

    const imgSrc = `${API_STATIC}${product.image}`;

    return (
        <div style={{ background: '#f8fafc', minHeight: '60vh', padding: '48px 0 80px', fontFamily: "'Open Sans', sans-serif" }}>
            <div className="container">

                {/* Breadcrumb */}
                <nav style={{ marginBottom: 32, fontSize: '0.85rem', color: '#6b7280' }}>
                    <Link to="/" style={{ color: '#1565c0', textDecoration: 'none' }}>Home</Link>
                    <span style={{ margin: '0 8px' }}>›</span>
                    <Link to="/products" style={{ color: '#1565c0', textDecoration: 'none' }}>Products</Link>
                    <span style={{ margin: '0 8px' }}>›</span>
                    <Link to={`/products?category=${encodeURIComponent(product.category)}`} style={{ color: '#1565c0', textDecoration: 'none' }}>
                        {product.category}
                    </Link>
                    <span style={{ margin: '0 8px' }}>›</span>
                    <span style={{ color: '#1a202c', fontWeight: 600 }}>{product.name}</span>
                </nav>

                {/* Card */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    alignItems: 'start',
                    background: '#fff',
                    borderRadius: 16,
                    boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
                    overflow: 'hidden',
                }}>

                    {/* Left — zoom image */}
                    <ImageZoom
                        src={imgSrc}
                        alt={product.name}
                        onClick={() => setLightboxOpen(true)}
                    />

                    {/* Right — details */}
                    <div style={{ padding: '40px 40px 40px 32px' }}>

                        <span style={{
                            display: 'inline-block', padding: '4px 12px',
                            background: '#eff6ff', color: '#1565c0', borderRadius: 50,
                            fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em',
                            textTransform: 'uppercase', marginBottom: 12,
                        }}>
                            {product.category}
                        </span>

                        <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1a202c', margin: '0 0 6px', lineHeight: 1.3 }}>
                            {product.name}
                        </h1>
                        <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: '0 0 20px' }}>
                            by <strong style={{ color: '#374151' }}>{product.brand}</strong>
                        </p>

                        <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1565c0', marginBottom: 8 }}>
                            ₹{product.price.toLocaleString('en-IN')}
                        </div>

                        <p style={{ fontSize: '0.85rem', marginBottom: 24 }}>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                color: product.qty > 0 ? '#16a34a' : '#dc2626', fontWeight: 600,
                            }}>
                                <span style={{
                                    width: 8, height: 8, borderRadius: '50%',
                                    background: product.qty > 0 ? '#16a34a' : '#dc2626',
                                    display: 'inline-block',
                                }} />
                                {product.qty > 0 ? `${product.qty} in stock` : 'Out of stock'}
                            </span>
                        </p>

                        <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '0 0 24px' }} />

                        {/* Material / Size / Color badges */}
                        {(product.material || product.size || product.color) && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 }}>
                                {product.material && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                                        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Material</span>
                                        <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1a202c' }}>{product.material}</span>
                                    </div>
                                )}
                                {product.size && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                                        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Size</span>
                                        <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1a202c' }}>{product.size}</span>
                                    </div>
                                )}
                                {product.color && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                                        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Color</span>
                                        <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1a202c' }}>{product.color}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div style={{ marginBottom: 20 }}>
                            <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                                Description
                            </h4>
                            <p style={{ fontSize: '0.95rem', color: '#4b5563', lineHeight: 1.7, margin: 0 }}>
                                {product.description}
                            </p>
                        </div>

                        <div style={{ marginBottom: 32 }}>
                            <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                                Usage
                            </h4>
                            <p style={{ fontSize: '0.95rem', color: '#4b5563', lineHeight: 1.7, margin: 0 }}>
                                {product.usage}
                            </p>
                        </div>

                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <button
                                onClick={handleAddToCart}
                                disabled={product.qty === 0}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                    padding: '12px 28px',
                                    background: added ? '#f0fdf4' : product.qty === 0 ? '#f1f5f9' : '#fff',
                                    color: added ? '#16a34a' : product.qty === 0 ? '#94a3b8' : '#1565c0',
                                    border: `1.5px solid ${added ? '#86efac' : product.qty === 0 ? '#e2e8f0' : '#1565c0'}`,
                                    borderRadius: 50, fontWeight: 600, fontSize: '0.95rem',
                                    cursor: product.qty === 0 ? 'default' : 'pointer',
                                    transition: 'all 0.15s',
                                }}
                            >
                                {added ? '✓ Added to Cart' : product.qty === 0 ? 'Out of Stock' : '+ Add to Cart'}
                            </button>
                            <button
                                onClick={handleWishlist}
                                title={wishlisted ? 'Remove from wishlist' : 'Save to wishlist'}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '12px 20px',
                                    background: wishlisted ? '#fef2f2' : '#fff',
                                    color: wishlisted ? '#ef4444' : '#6b7280',
                                    border: `1.5px solid ${wishlisted ? '#fca5a5' : '#d1d5db'}`,
                                    borderRadius: 50, fontWeight: 600, fontSize: '0.95rem',
                                    cursor: 'pointer', transition: 'all 0.15s',
                                }}
                            >
                                {wishlisted ? '♥' : '♡'} {wishlisted ? 'Saved' : 'Wishlist'}
                            </button>
                            <Link
                                to="/request-a-quote"
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                    padding: '12px 28px', background: '#1565c0', color: '#fff',
                                    borderRadius: 50, fontWeight: 600, fontSize: '0.95rem',
                                    textDecoration: 'none',
                                }}
                            >
                                Request a Quote →
                            </Link>
                            <Link
                                to="/products"
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                    padding: '12px 24px', border: '1.5px solid #d1d5db', color: '#374151',
                                    borderRadius: 50, fontWeight: 600, fontSize: '0.95rem',
                                    textDecoration: 'none', background: '#fff',
                                }}
                            >
                                ← Back to Products
                            </Link>
                        </div>
                    </div>
                </div>

            </div>

            {/* Specifications table */}
            {(() => {
                const specs: { label: string; value: string | number }[] = [
                    { label: 'Application / Usage', value: product.application ?? '' },
                    { label: 'Material',             value: product.material    ?? '' },
                    { label: 'Color',                value: product.color       ?? '' },
                    { label: 'Size',                 value: product.size        ?? '' },
                    { label: 'Units',                value: product.countUnit   ?? '' },
                    { label: 'Weight & Mass',        value: product.weightUnit  ?? '' },
                    { label: 'Length & Distance',    value: product.lengthUnit  ?? '' },
                ];
                const visible = specs.filter(s => String(s.value).trim());
                if (!visible.length) return null;
                return (
                    <div style={{
                        marginTop: 24,
                        background: '#fff',
                        borderRadius: 16,
                        boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
                        padding: '32px 40px',
                    }}>
                        <h3 style={{
                            fontSize: '1rem',
                            fontWeight: 700,
                            color: '#1a202c',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            marginBottom: 20,
                        }}>
                            Product Specifications
                        </h3>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                                {visible.map((spec, i) => (
                                    <tr key={spec.label} style={{ borderBottom: i < visible.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                        <td style={{
                                            padding: '12px 0',
                                            width: '35%',
                                            fontSize: '0.85rem',
                                            fontWeight: 700,
                                            color: '#64748b',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.06em',
                                            verticalAlign: 'top',
                                        }}>
                                            {spec.label}
                                        </td>
                                        <td style={{
                                            padding: '12px 0 12px 24px',
                                            fontSize: '0.95rem',
                                            color: '#1a202c',
                                            fontWeight: 500,
                                            verticalAlign: 'top',
                                        }}>
                                            {spec.value}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            })()}

            {/* Lightbox */}
            {lightboxOpen && (
                <Lightbox
                    src={imgSrc}
                    alt={product.name}
                    onClose={() => setLightboxOpen(false)}
                />
            )}

            <style>{`
                @media (max-width: 768px) {
                    .container > div[style*="grid-template-columns"] {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default ProductDetailPage;
